export const CART_AVAILABILITY_INDEX_STORAGE_KEY = "cartAvailabilityIndex";

const CART_PRODUCTS_KEY = "shoppingCart";
const CART_COMBOS_KEY = "shoppingCartCombos";

const safeParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const getProductId = (item = {}) => normalizeId(
  item.group_id ?? item.id ?? item.product_id
);

const getComboId = (item = {}) => normalizeId(
  item.combo_id ?? item.id
);

const hasOwn = (item, key) => Object.prototype.hasOwnProperty.call(item || {}, key);

const isFlagEnabled = (value) => (
  value === true ||
  value === 1 ||
  String(value).trim().toLowerCase() === "true" ||
  String(value).trim() === "1"
);

const isExplicitlyDisabled = (item = {}) => (
  (hasOwn(item, "status") && !isFlagEnabled(item.status)) ||
  (hasOwn(item, "available_ecommerce") && !isFlagEnabled(item.available_ecommerce))
);

const isAvailableForEcommerce = (item = {}) => !isExplicitlyDisabled(item);

const getCurrentDayKey = () => {
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return days[new Date().getDay()];
};

const timeToMinutes = (time = "") => {
  const [hours, minutes] = String(time).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};

const isCurrentTimeInsideRange = (minHour, maxHour) => {
  const start = timeToMinutes(minHour);
  const end = timeToMinutes(maxHour);
  if (start === null || end === null) return true;

  const now = new Date();
  const current = (now.getHours() * 60) + now.getMinutes();

  if (end < start) {
    return current >= start || current < end;
  }

  return current >= start && current < end;
};

const getDataPayload = (generalData) => {
  if (!generalData || generalData === "error") return null;
  if (Array.isArray(generalData)) {
    const storedBranch = safeParse(localStorage.getItem("branchoffice"), null);
    const branchId = storedBranch?.id;

    if (branchId) {
      const scoped = generalData.find(item => String(item?.branchoffice_id) === String(branchId));
      if (scoped?.data) return scoped.data;
    }

    return generalData[0]?.data || null;
  }

  return generalData?.data || generalData;
};

const getActiveCategories = (data = {}) => {
  const categories = asArray(data.category).filter(category => category?.code === "P");
  const activeCatalogs = asArray(data.catalogs)
    .filter(catalog => isFlagEnabled(catalog?.status))
    .filter(catalog => isFlagEnabled(catalog?.[getCurrentDayKey()]))
    .filter(catalog => isCurrentTimeInsideRange(catalog?.minHour, catalog?.maxHour));

  if (activeCatalogs.length === 0) return categories;

  const categoryIds = new Set();
  activeCatalogs.forEach(catalog => {
    asArray(catalog.categories).forEach(category => {
      const id = normalizeId(category?.category_id);
      if (id) categoryIds.add(id);
    });
  });

  return categories.filter(category => categoryIds.has(normalizeId(category?.category_id)));
};

const collectSelectedComboProductIds = (combo = {}) => {
  const ids = [];

  asArray(combo.setGroups).forEach(setGroup => {
    asArray(setGroup.groups).forEach(group => {
      const id = getProductId(group);
      if (id) ids.push(id);
    });

    asArray(setGroup.groupsselected).forEach(group => {
      const id = getProductId(group);
      if (id) ids.push(id);
    });
  });

  return ids;
};

const markProductKnown = (index, item = {}) => {
  const id = getProductId(item);
  if (!id) return;

  const current = index.productAvailabilityById[id];
  const available = isAvailableForEcommerce(item);
  index.productAvailabilityById[id] = current === false ? false : available;
  index.productNamesById[id] = item.name || item.group_name || index.productNamesById[id] || "Producto";
};

const markComboKnown = (index, item = {}) => {
  const id = getComboId(item);
  if (!id) return;

  const current = index.comboAvailabilityById[id];
  const available = isAvailableForEcommerce(item);
  index.comboAvailabilityById[id] = current === false ? false : available;
  index.comboNamesById[id] = item.name || item.combo_name || index.comboNamesById[id] || "Combo";
};

export const buildMenuAvailabilityIndex = (generalData) => {
  const data = getDataPayload(generalData);
  const index = {
    hasMenuData: false,
    activeProductIds: [],
    activeComboIds: [],
    productAvailabilityById: {},
    comboAvailabilityById: {},
    productNamesById: {},
    comboNamesById: {},
    updatedAt: Date.now()
  };

  if (!data) return index;

  asArray(data.groups).forEach(group => {
    index.hasMenuData = true;
    markProductKnown(index, group);
  });

  getActiveCategories(data).forEach(category => {
    asArray(category.groups).forEach(group => {
      index.hasMenuData = true;
      markProductKnown(index, group);

      const id = getProductId(group);
      if (id && isAvailableForEcommerce(group)) {
        index.activeProductIds.push(id);
        index.productAvailabilityById[id] = index.productAvailabilityById[id] === false ? false : true;
      }
    });

    asArray(category.combos).forEach(combo => {
      index.hasMenuData = true;
      markComboKnown(index, combo);

      const id = getComboId(combo);
      if (id && isAvailableForEcommerce(combo)) {
        index.activeComboIds.push(id);
        index.comboAvailabilityById[id] = index.comboAvailabilityById[id] === false ? false : true;
      }
    });
  });

  index.activeProductIds = Array.from(new Set(index.activeProductIds));
  index.activeComboIds = Array.from(new Set(index.activeComboIds));

  return index;
};

export const persistMenuAvailabilityIndex = (generalData) => {
  const index = buildMenuAvailabilityIndex(generalData);

  if (index.hasMenuData) {
    localStorage.setItem(CART_AVAILABILITY_INDEX_STORAGE_KEY, JSON.stringify(index));
  }

  return index;
};

export const getMenuAvailabilityIndex = (generalData) => {
  const index = buildMenuAvailabilityIndex(generalData);
  if (index.hasMenuData) return index;

  return safeParse(localStorage.getItem(CART_AVAILABILITY_INDEX_STORAGE_KEY), index) || index;
};

const getRemovedItemInfo = (item = {}, namesById = {}, fallbackName = "Producto") => {
  const id = getProductId(item) || getComboId(item);
  return {
    id,
    name: item.name || item.group_name || item.combo_name || namesById[id] || fallbackName
  };
};

export const sanitizeCartAgainstMenuIndex = ({ products = [], combos = [], index }) => {
  if (!index?.hasMenuData) {
    return {
      products,
      combos,
      removedProducts: [],
      removedCombos: [],
      changed: false
    };
  }

  const activeProductIds = new Set(index.activeProductIds || []);
  const activeComboIds = new Set(index.activeComboIds || []);
  const productAvailability = index.productAvailabilityById || {};
  const comboAvailability = index.comboAvailabilityById || {};

  const removedProducts = [];
  const nextProducts = products.filter(product => {
    const id = getProductId(product);
    const keep = id && activeProductIds.has(id) && productAvailability[id] !== false;

    if (!keep) {
      removedProducts.push(getRemovedItemInfo(product, index.productNamesById, "Producto"));
    }

    return keep;
  });

  const removedCombos = [];
  const nextCombos = combos.filter(combo => {
    const id = getComboId(combo);
    const comboEnabled = id && activeComboIds.has(id) && comboAvailability[id] !== false;
    const comboProductsEnabled = collectSelectedComboProductIds(combo)
      .every(productId => productAvailability[productId] !== false);
    const keep = comboEnabled && comboProductsEnabled;

    if (!keep) {
      removedCombos.push(getRemovedItemInfo(combo, index.comboNamesById, "Combo"));
    }

    return keep;
  });

  return {
    products: nextProducts,
    combos: nextCombos,
    removedProducts,
    removedCombos,
    changed: removedProducts.length > 0 || removedCombos.length > 0
  };
};

export const sanitizeStoredCartAgainstMenu = (generalData) => {
  const products = safeParse(localStorage.getItem(CART_PRODUCTS_KEY), []) || [];
  const combos = safeParse(localStorage.getItem(CART_COMBOS_KEY), []) || [];
  let index = buildMenuAvailabilityIndex(generalData);

  if (index.hasMenuData) {
    localStorage.setItem(CART_AVAILABILITY_INDEX_STORAGE_KEY, JSON.stringify(index));
  } else {
    const storedGeneralData = safeParse(localStorage.getItem("generalData"), []) || [];
    index = getMenuAvailabilityIndex(storedGeneralData);
  }

  const result = sanitizeCartAgainstMenuIndex({ products, combos, index });

  if (result.changed) {
    localStorage.setItem(CART_PRODUCTS_KEY, JSON.stringify(result.products));
    localStorage.setItem(CART_COMBOS_KEY, JSON.stringify(result.combos));
  }

  return result;
};

export const getCartAvailabilityMessage = (result = {}) => {
  const removed = [
    ...asArray(result.removedProducts),
    ...asArray(result.removedCombos)
  ];

  if (removed.length === 0) return "";

  const names = removed.map(item => item.name).filter(Boolean);
  const visibleNames = names.slice(0, 2).join(", ");
  const remaining = Math.max(names.length - 2, 0);
  const suffix = remaining > 0 ? ` y ${remaining} mas` : "";

  return `Actualizamos tu carrito porque ${visibleNames}${suffix} ya no esta disponible.`;
};
